/** English - `content`: pages, blog, menus, metafields and metaobjects. See `./common.ts` for conventions. */
const content: Record<string, string> = {
  // ---- pages, blog, menus ----
  // Pages list
  'sort.title_asc': 'Title A–Z',
  'sort.title_desc': 'Title Z–A',
  'sort.updated_desc': 'Recently edited',
  'sort.updated_asc': 'Longest untouched',
  'sort.published_desc': 'Recently published',
  'sort.created_desc': 'Newest first',
  'sort.recent': 'Newest first',
  'sort.oldest': 'Oldest first',

  'pages.search_label': 'Search pages',
  'pages.search_placeholder': 'Search by title or web address',
  'pages.select_row': 'Select {title}',
  'pages.preview': 'Preview {title} on your shop',
  'pages.design': 'Design',
  'pages.design_label': 'Design {title} in the theme editor',
  'pages.column.handle': 'Web address',
  'pages.column.published': 'Published',
  'pages.kpi.total': 'Pages',
  'pages.kpi.scheduled_hint': 'Dated ahead, not live yet',
  'pages.bulk.publish': 'Publish',
  'pages.bulk.unpublish': 'Unpublish',
  'pages.bulk.delete_title': 'Delete pages',
  'pages.bulk.delete_message_one': 'Delete {count} page? Any link to it will stop working.',
  'pages.bulk.delete_message_other': 'Delete {count} pages? Any link to them will stop working.',
  'pages.toast.published_one': '{count} page published.',
  'pages.toast.published_other': '{count} pages published.',
  'pages.toast.unpublished_one': '{count} page unpublished.',
  'pages.toast.unpublished_other': '{count} pages unpublished.',
  'pages.toast.deleted_one': '{count} page deleted.',
  'pages.toast.deleted_other': '{count} pages deleted.',

  'blog.search_label': 'Search posts',
  'blog.search_placeholder': 'Search by title or excerpt',
  'blog.select_row': 'Select {title}',
  'blog.preview': 'Preview {title} on your shop',
  'blog.column.author': 'Author',
  'blog.column.tags': 'Tags',
  'blog.filters.any_author': 'Any author',
  'blog.filters.published': 'Published',
  'blog.kpi.total': 'Posts',
  'blog.kpi.scheduled_hint': 'Dated ahead, not live yet',
  'blog.bulk.publish': 'Publish',
  'blog.bulk.unpublish': 'Unpublish',
  'blog.bulk.delete_title': 'Delete posts',
  'blog.bulk.delete_message_one': 'Delete {count} post? Any link to it will stop working.',
  'blog.bulk.delete_message_other': 'Delete {count} posts? Any link to them will stop working.',
  'blog.toast.published_one': '{count} post published.',
  'blog.toast.published_other': '{count} posts published.',
  'blog.toast.unpublished_one': '{count} post unpublished.',
  'blog.toast.unpublished_other': '{count} posts unpublished.',
  'blog.toast.deleted_one': '{count} post deleted.',
  'blog.toast.deleted_other': '{count} posts deleted.',

  'pages.title': 'Pages',
  'pages.count_one': '{count} page',
  'pages.count_other': '{count} pages',
  'pages.add_page': 'Add page',
  'pages.load_failed': 'Pages are unavailable right now. Try again in a moment.',
  'pages.empty_title': 'No pages yet',
  'pages.empty_description':
    "Pages are everything on your storefront that isn't a product - your story, your policies, how to reach you.",
  'pages.add_first': 'Add your first page',
  'pages.column.page': 'Page',
  'pages.column.status': 'Status',
  'pages.column.sections': 'Sections',
  'pages.column.last_saved': 'Last saved',
  'pages.status.scheduled': 'Scheduled',
  'pages.field.title': 'Page title',

  // New page dialog
  'pages.new.title': 'Add a page',
  'pages.new.creating': 'Creating…',
  'pages.new.create': 'Create page',
  'pages.new.create_failed': 'Could not create the page.',
  'pages.new.title_hint': 'Shoppers see this at the top of the page, and its web address is built from it.',
  'pages.new.start_from': 'Start from',

  // Page editor
  'pages.editor.loading': 'Loading the page…',
  'pages.editor.not_found_title': 'Page not found',
  'pages.editor.not_found_description': 'It may have been deleted, or it belongs to another store.',
  'pages.editor.back': 'Back to pages',
  'pages.editor.untitled': 'Untitled page',
  'pages.editor.view_page': 'View page',
  'pages.editor.publish': 'Publish',
  'pages.editor.save_failed': 'Could not save the page.',
  'pages.editor.delete_failed': 'Could not delete the page.',
  'pages.editor.saved_live': 'Saved - your page is live.',
  'pages.editor.draft_saved': 'Draft saved.',
  'pages.editor.live_unsaved': 'This page is live. Nothing changes for shoppers until you save.',
  'pages.editor.read_only': 'You have read-only access to store settings, so these settings cannot be changed.',
  'pages.editor.show_title': 'Show the title on the page',
  'pages.editor.show_title_hint':
    'Turn this off when one of your sections already carries the heading. The page is still called this everywhere else.',
  'pages.editor.visibility': 'Visibility',
  'pages.editor.hidden': 'Hidden',
  'pages.editor.visible': 'Visible',
  'pages.editor.visibility_hint': 'Hidden pages stay at their web address for you and 404 for everyone else.',
  'pages.editor.visible_from': 'Visible from',
  'pages.editor.visible_from_hint':
    'Set a date ahead to schedule the page. Blank publishes it as soon as you save.',
  'pages.editor.seo_heading': 'Search engine listing',
  'pages.editor.seo_description': 'How this page reads in search results. Leave blank to use the page title.',
  'pages.editor.seo_title': 'SEO title',
  'pages.editor.meta_description': 'Meta description',
  'pages.editor.web_address': 'Web address',
  'pages.editor.web_address_hint': 'Fixed once the page exists - menus, emails and bookmarks point at it.',
  'pages.editor.delete_confirm': 'Delete "{title}"? This cannot be undone.',
  'pages.editor.delete': 'Delete page',
  'pages.editor.design': 'Design in theme editor',
  'pages.editor.design_hint':
    "The page's sections are arranged in the theme editor, alongside the rest of your shop. Its title, visibility and search listing are set here.",

  // Page templates
  'templates.about.label': 'About us',
  'templates.about.description': 'Your story, with room for a photograph beside it.',
  'templates.contact.label': 'Contact',
  'templates.contact.description': 'A form that emails you, the same one every store starts with.',
  'templates.policy.label': 'Policy',
  'templates.policy.description': 'Shipping, returns, privacy or terms - the pages you must have.',
  'templates.faq.label': 'Questions & answers',
  'templates.faq.description': 'The questions shoppers ask before they buy.',
  'templates.blank.label': 'Blank page',
  'templates.blank.description': 'Start with nothing and add the sections you want.',

  // Blog list
  'blog.title': 'Blog',
  'blog.count_one': '{count} post',
  'blog.count_other': '{count} posts',
  'blog.blogs': 'Blogs',
  'blog.write_post': 'Write a post',
  'blog.start_failed': 'Could not start a new post.',
  'blog.all_blogs': 'All blogs',
  'blog.load_failed': 'The blog is unavailable right now. Try again in a moment.',
  'blog.empty_title': 'No posts yet',
  'blog.empty_description':
    'Write about what you sell and why. Posts bring people to your shop from search and social, long after you publish them.',
  'blog.write_first': 'Write your first post',
  'blog.column.post': 'Post',
  'blog.column.status': 'Status',
  'blog.column.blog': 'Blog',
  'blog.column.date': 'Date',
  'blog.status.scheduled': 'Scheduled',

  // Manage blogs dialog
  'blog.manage.your_blogs': 'Your blogs',
  'blog.manage.delete_blocked': 'Move or delete this blog’s posts first.',
  'blog.manage.delete_confirm': 'Delete the blog "{title}"?',
  'blog.manage.empty': 'No blogs yet.',
  'blog.manage.delete_failed': 'Could not delete that blog.',
  'blog.manage.add': 'Add a blog',
  'blog.manage.name': 'Name',
  'blog.manage.name_placeholder': 'Recipes',
  'blog.manage.name_hint': 'Its web address is built from this and cannot change later.',
  'blog.manage.description': 'Description',
  'blog.manage.create_failed': 'Could not create that blog.',
  'blog.manage.creating': 'Creating…',
  'blog.manage.create': 'Create blog',

  // Blog post form
  'blog.post.tags': 'Tags',
  'blog.post.tags_placeholder': 'Add a tag and press Enter',
  'blog.post.tags_hint': 'Readers use these to find related posts.',
  'blog.post.remove_tag': 'Remove tag {tag}',
  'blog.post.saved_live': 'Saved - your post is live.',
  'blog.post.draft_saved': 'Draft saved.',
  'blog.post.loading': 'Loading the post…',
  'blog.post.back': 'Back to the blog',
  'blog.post.untitled': 'Untitled post',
  'blog.post.new': 'New post',
  'blog.post.view': 'View post',
  'blog.post.publish': 'Publish',
  'blog.post.save_draft': 'Save draft',
  'blog.post.save_failed': 'Could not save the post.',
  'blog.post.delete_failed': 'Could not delete the post.',
  'blog.post.read_only': 'You have read-only access, so this is a preview only.',
  'blog.post.title': 'Title',
  'blog.post.title_placeholder': 'What are you writing about?',
  'blog.post.content': 'Content',
  'blog.post.content_placeholder': 'Write your post…',
  'blog.post.seo_heading': 'Search engine listing',
  'blog.post.seo_description': 'How this post reads in search results. Leave blank to use the title and excerpt.',
  'blog.post.seo_title': 'SEO title',
  'blog.post.meta_description': 'Meta description',
  'blog.post.visibility': 'Visibility',
  'blog.post.hidden': 'Hidden',
  'blog.post.visible': 'Visible',
  'blog.post.visible_from': 'Visible from',
  'blog.post.visible_from_hint': 'Set a date ahead to schedule the post.',
  'blog.post.blog': 'Blog',
  'blog.post.cover_image': 'Cover image',
  'blog.post.cover_image_hint': 'Shown on the blog index and at the top of the post.',
  'blog.post.cover_frame_card': 'Card on the blog page',
  'blog.post.cover_frame_article': 'Top of the post',
  'blog.post.excerpt': 'Excerpt',
  'blog.post.excerpt_hint':
    'The summary on the blog index. The first line of a post is rarely the line that sells it.',
  'blog.post.author': 'Author',
  'blog.post.author_hint': 'The byline. Leave blank for none.',
  'blog.post.delete_confirm': 'Delete "{title}"? This cannot be undone.',
  'blog.post.delete': 'Delete post',

  // Menus list
  'menus.title': 'Menus',
  'menus.count_one': '{count} menu',
  'menus.count_other': '{count} menus',
  'menus.create': 'Create menu',
  'menus.load_failed': 'Menus are unavailable right now. Try again in a moment.',
  'menus.empty_title': 'No menus yet',
  'menus.empty_description':
    'Menus are the links across your storefront - a main menu in the header, a shorter one in the footer.',
  'menus.create_first': 'Create your first menu',
  'menus.column.menu': 'Menu',
  'menus.menu_items': 'Menu items',
  'menus.no_links': 'No links yet',

  // Menu form
  'menus.destination.home': 'Home',
  'menus.destination.all_products': 'All products',
  'menus.destination.cart': 'Cart',
  'menus.destination.account': 'Account',
  'menus.destination.search': 'Search',
  'menus.destination.careers': 'Careers',
  'menus.item.label': 'Label',
  'menus.item.label_placeholder': 'Home',
  'menus.item.link': 'Link',
  'menus.item.this_item': 'this item',
  'menus.item.add_nested_label': 'Add an item under {label}',
  'menus.item.add_nested': 'Add nested item',
  'menus.item.remove_label': 'Remove {label}',
  'menus.form.load_failed': 'That menu could not be loaded.',
  'menus.form.back': 'Back to menus',
  'menus.form.add_title': 'Add menu',
  'menus.form.name': 'Name',
  'menus.form.name_placeholder': 'Main menu',
  'menus.form.handle_hint': 'Handle: {handle} - themes link to this, so it does not change.',
  'menus.form.handle_new_hint': 'A handle is built from the name when you save.',
  'menus.form.items_description':
    'Each item is a label and where it goes. Start typing in a link field to pick one of your pages or blogs, or write a path like {path} or a full address. Items may nest up to {depth} levels.',
  'menus.form.add_item': 'Add menu item',
  'menus.form.save_failed': 'Could not save this menu.',
  'menus.form.delete_failed': 'Could not delete this menu.',
  'menus.form.delete_confirm': 'Delete this menu?',
  'menus.form.keep': 'Keep',
  'menus.form.delete': 'Delete menu',
  // ---- end pages, blog, menus ----

  // ---- metafields, metaobjects ----
  'field_types.single_line_text': 'Single line text',
  'field_types.multi_line_text': 'Multi-line text',
  'field_types.number': 'Number',
  'field_types.boolean': 'True or false',
  'field_types.url': 'URL',
  'field_types.color': 'Colour',
  'field_types.date': 'Date',
  'field_types.metaobject_reference': 'Metaobject',
  'field_types.list': '{type} (List)',

  'metafields.title': 'Metafields',
  'metafields.owner_product': 'Product',
  'metafields.add': 'Add metafield',
  'metafields.creating': 'Creating…',
  'metafields.name': 'Name',
  'metafields.name_placeholder': 'Colour',
  'metafields.name_hint': 'A key is built from the name; products and themes address the field by it.',
  'metafields.type': 'Type',
  'metafields.reference_needs_definition':
    'Create a metaobject definition first - a reference field needs a kind of entry to point at.',
  'metafields.points_at': 'Points at',
  'metafields.choose_metaobject': 'Choose a metaobject…',
  'metafields.list_of_values': 'List of values',
  'metafields.list_of_values_hint': 'A product may hold several, in order.',
  'metafields.storefront': 'Available on storefront',
  'metafields.storefront_hint': 'Send it to shoppers with the product.',
  'metafields.create_failed': 'Could not create this field.',
  'metafields.delete_failed': 'Could not delete that field.',
  'metafields.empty_title': 'No metafields yet',
  'metafields.empty_description':
    'Metafields are the custom data your products carry - a colour, a care instruction, a size chart. Point one at a metaobject to turn it into a pick-list.',
  'metafields.on_storefront': 'On storefront',
  'metafields.internal': 'Internal',
  'metafields.confirm_delete': 'Delete {name} everywhere?',
  'metafields.keep': 'Keep',
  'metafields.delete_named': 'Delete {name}',
  'metafields.boolean.not_set': 'Not set',
  'metafields.boolean.true': 'True',
  'metafields.boolean.false': 'False',
  'metafields.product.unknown_entry': 'Unknown entry',
  'metafields.product.entry_fallback': 'entry',
  'metafields.product.remove_named': 'Remove {name}',
  'metafields.product.add_to': 'Add to {name}',
  'metafields.product.add_another': 'Add another…',
  'metafields.product.choose': 'Choose {name}…',
  'metafields.product.draft_suffix': '(draft)',
  'metafields.product.no_entries': 'No {name} yet.',
  'metafields.product.entries_fallback': 'entries',
  'metafields.product.create_under_content': 'Create one under Content',
  'metafields.product.create_under_content_end': '.',
  'metafields.product.one_per_line': 'One value per line.',

  'metaobjects.title': 'Metaobjects',
  'metaobjects.add_definition': 'Add definition',
  'metaobjects.creating': 'Creating…',
  'metaobjects.name': 'Name',
  'metaobjects.name_placeholder': 'Colour swatch',
  'metaobjects.name_hint': 'A handle is built from the name; themes refer to it, so it never changes.',
  'metaobjects.description': 'Description',
  'metaobjects.description_placeholder': 'What is this type of content for?',
  'metaobjects.storefront': 'Available on storefront',
  'metaobjects.storefront_hint': 'Let your theme read entries of this type.',
  'metaobjects.create_failed': 'Could not create this definition.',
  'metaobjects.summary_definitions': 'Definitions',
  'metaobjects.definitions_count_one': '{count} definition',
  'metaobjects.definitions_count_other': '{count} definitions',
  'metaobjects.entries_count_one': '{count} entry',
  'metaobjects.entries_count_other': '{count} entries',
  'metaobjects.recent_entries': 'Recent entries',
  'metaobjects.nothing_created': 'Nothing has been created yet.',
  'metaobjects.definition_fallback': 'Metaobject',
  'metaobjects.search_label': 'Search definitions',
  'metaobjects.search_placeholder': 'Searching in metaobject definitions…',
  'metaobjects.definitions_unavailable': 'Definitions are unavailable right now. Try again in a moment.',
  'metaobjects.no_definitions': 'No definitions found',
  'metaobjects.empty_description':
    'A definition describes a kind of content - a colour swatch, a size chart, an author profile - that entries are then made from.',
  'metaobjects.add_first': 'Add your first definition',
  'metaobjects.try_other_search': 'Try changing the search term',
  'metaobjects.on_storefront': 'On storefront',
  'metaobjects.back': 'Back to metaobjects',
  'metaobjects.keep': 'Keep',
  'metaobjects.status': 'Status',
  'metaobjects.boolean.not_set': 'Not set',
  'metaobjects.boolean.true': 'True',
  'metaobjects.boolean.false': 'False',
  'metaobjects.entry.load_failed': 'That entry could not be loaded.',
  'metaobjects.entry.references_count_one': '{count} reference',
  'metaobjects.entry.references_count_other': '{count} references',
  'metaobjects.entry.label': 'Label',
  'metaobjects.entry.label_hint': 'The display name - what this entry is called in lists.',
  'metaobjects.entry.no_fields':
    'This definition has no fields yet. Add one to give entries something to hold.',
  'metaobjects.entry.references': 'References',
  'metaobjects.entry.no_references':
    'Nothing uses this entry yet. Add it to a product from a metafield of this type.',
  'metaobjects.entry.reference_product': 'Product',
  'metaobjects.entry.handle': 'Handle',
  'metaobjects.entry.handle_hint': 'Themes address this entry by its handle, so it does not change.',
  'metaobjects.entry.save_failed': 'Could not save this entry.',
  'metaobjects.entry.saved': 'Saved.',
  'metaobjects.entry.delete_failed': 'Could not delete this entry.',
  'metaobjects.entry.confirm_delete': 'Delete this entry?',
  'metaobjects.entry.confirm_delete_used_one': 'Delete this entry? {count} product would lose it.',
  'metaobjects.entry.confirm_delete_used_other': 'Delete this entry? {count} products would lose it.',
  'metaobjects.entry.delete': 'Delete entry',
  'metaobjects.definition.load_failed': 'That definition could not be loaded.',
  'metaobjects.definition.fields': 'Fields',
  'metaobjects.definition.fields_description':
    'What every entry of this type holds. Removing a field hides its values from new entry forms; the values already saved are dropped the next time an entry is edited.',
  'metaobjects.definition.no_fields': 'No fields yet.',
  'metaobjects.definition.type': 'Type',
  'metaobjects.definition.field_key_hint': 'Key: {key}',
  'metaobjects.definition.remove_field': 'Remove {name}',
  'metaobjects.definition.add_field': 'Add field',
  'metaobjects.definition.save_fields': 'Save fields',
  'metaobjects.definition.save_fields_failed': 'Could not save these fields.',
  'metaobjects.definition.new_entry_title': 'New {name}',
  'metaobjects.definition.entry_name_placeholder': 'Beige',
  'metaobjects.definition.add_entry': 'Add entry',
  'metaobjects.definition.details': 'Details',
  'metaobjects.definition.no_description': 'No description.',
  'metaobjects.definition.edit_details': 'Edit details',
  'metaobjects.definition.entries': 'Entries',
  'metaobjects.definition.entries_of_type_one': '{count} entry of this type.',
  'metaobjects.definition.entries_of_type_other': '{count} entries of this type.',
  'metaobjects.definition.no_entries': 'No entries yet.',
  'metaobjects.definition.add_field_first': 'Add a field first, then create one.',
  'metaobjects.definition.open_named': 'Open {name}',
  'metaobjects.definition.delete_named': 'Delete {name}',
  'metaobjects.definition.confirm_delete_one': 'Delete {name}? Its {count} entry go with it.',
  'metaobjects.definition.confirm_delete_other': 'Delete {name}? Its {count} entries go with it.',
  'metaobjects.definition.delete': 'Delete definition',
  'metaobjects.definition.delete_failed': 'Could not delete this definition.',
  // ---- end metafields, metaobjects ----
};

export default content;
